import { Badge, Button, Input, Spinner } from '@axioma/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Copy, Download, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { api, extractErrorMessage } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface SetupResponse {
  otpauthUrl: string;
  qrCodeDataUrl: string;
  recoveryCodes: string[];
}

export function TwoFactorSetup(): JSX.Element {
  const pendingToken = useAuthStore((s) => s.pendingToken);
  const setToken = useAuthStore((s) => s.setToken);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const setup = useQuery({
    queryKey: ['2fa', 'setup'],
    queryFn: async () => {
      const { data } = await api.post<SetupResponse>('/auth/2fa/setup');
      return data;
    },
    enabled: Boolean(pendingToken),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const verify = useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<{ token: string }>('/auth/2fa/verify', { code });
      return data;
    },
    onSuccess: (data) => {
      setToken(data.token);
      navigate('/', { replace: true });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  useEffect(() => {
    if (copied) {
      const t = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [copied]);

  if (!pendingToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="ax-hero-bg flex min-h-screen w-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(229,165,71,0.40)] bg-ax-gradient-signature-soft">
            <ShieldCheck size={20} className="text-ax-aurora" />
          </div>
          <h1 className="mb-1 font-display text-3xl tracking-tight text-ax-text-primary">
            Configurar 2FA
          </h1>
          <p className="ax-body text-ax-text-secondary">
            Olá, <strong className="text-ax-text-primary">{user?.name}</strong>. Para acessar a
            AXIOMA, configure 2FA — é obrigatório, sem opt-out.
          </p>
        </div>

        {setup.isLoading && (
          <div className="rounded-ax-lg border border-ax-line bg-ax-graphite p-12 text-center">
            <Spinner size="lg" variant="aurora" />
          </div>
        )}

        {setup.isError && (
          <div className="rounded-ax-lg border border-ax-critical bg-ax-graphite p-6 text-center">
            <p className="text-ax-critical">{extractErrorMessage(setup.error)}</p>
          </div>
        )}

        {setup.data && (
          <div className="space-y-7 rounded-ax-lg border border-ax-line bg-ax-graphite p-8">
            <section>
              <h2 className="ax-eyebrow mb-3 text-ax-text-tertiary">1. Escaneie o QR Code</h2>
              <div className="flex flex-col items-start gap-6 md:flex-row">
                <div className="flex-shrink-0 rounded-ax-md bg-ax-ivory p-3">
                  <img
                    src={setup.data.qrCodeDataUrl}
                    alt="QR Code de configuração 2FA"
                    className="block h-48 w-48"
                    style={{ width: 192, height: 192 }}
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="ax-body text-ax-text-secondary">
                    Use Google Authenticator, 1Password, Authy, Bitwarden, ou qualquer app TOTP
                    padrão. Escaneie o código ao lado.
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                    Ou cole esta URL no app:
                  </p>
                  <div className="flex min-w-0 items-center gap-2">
                    <code
                      className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-ax-sm border border-ax-line bg-ax-night px-2 py-1.5 font-mono text-[11px] text-ax-text-secondary"
                      title={setup.data.otpauthUrl}
                    >
                      {setup.data.otpauthUrl}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(setup.data.otpauthUrl);
                        setCopied(true);
                      }}
                      leftIcon={<Copy size={12} />}
                    >
                      {copied ? 'Copiado' : 'Copiar'}
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="ax-eyebrow mb-3 text-ax-text-tertiary">
                2. Salve seus códigos de recuperação
              </h2>
              <p className="ax-body-sm mb-3 text-ax-text-tertiary">
                Cada código pode ser usado uma única vez no lugar do TOTP. Guarde-os em local seguro
                — não será possível recuperá-los depois.
              </p>
              <div className="mb-3 grid grid-cols-2 gap-2">
                {setup.data.recoveryCodes.map((c) => (
                  <code
                    key={c}
                    className="rounded-ax-sm border border-ax-line bg-ax-night px-3 py-2 font-mono text-[13px] tracking-wider text-ax-text-primary"
                  >
                    {c}
                  </code>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Download size={14} />}
                onClick={() =>
                  downloadRecoveryCodes(setup.data!.recoveryCodes, user?.email ?? 'axioma')
                }
              >
                Baixar como .txt
              </Button>
            </section>

            <section>
              <h2 className="ax-eyebrow mb-3 text-ax-text-tertiary">
                3. Insira o código gerado pelo app
              </h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setError(null);
                  verify.mutate(code);
                }}
                className="flex items-end gap-3"
              >
                <div className="flex-1">
                  <Input
                    label="Código TOTP (6 dígitos)"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    mono
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    error={error ?? undefined}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  variant="signature"
                  size="lg"
                  loading={verify.isPending}
                  disabled={code.length !== 6}
                >
                  Confirmar
                </Button>
              </form>
              <Badge variant="aurora" className="mt-3">
                Não use este código novamente após confirmar — peça outro ao app
              </Badge>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function downloadRecoveryCodes(codes: string[], userIdentifier: string): void {
  const ts = new Date().toISOString().slice(0, 10);
  const content = `AXIOMA · Códigos de Recuperação 2FA
Usuário: ${userIdentifier}
Gerados em: ${ts}

⚠ Cada código pode ser usado APENAS UMA VEZ. Guarde em local seguro.

${codes.map((c, i) => `  ${String(i + 1).padStart(2, '0')}. ${c}`).join('\n')}

Em caso de perda do segundo fator, use estes códigos para acessar a plataforma.
Não compartilhe.`;
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `axioma-recovery-codes-${ts}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
